import React, { useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Inbox, ShieldCheck, Eye, Clock, Reply, CheckCheck } from "lucide-react";
import { useMediaQuery } from "@/hooks/use-media-query";
import { InboxList, useInbox, useInboxFilter, filterItems, keyPath, markSeen, archiveItems, TYPES } from "@/components/inbox/InboxList";
import { InboxReader } from "@/components/inbox/InboxReader";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { invalidate } from "@/hooks/use-resource";

const ICON = { request: Inbox, reply: Reply, approval: ShieldCheck, quote: Eye, followup: Clock };

function Empty({ data, onPick }) {
  const counts = data?.counts || {};
  const types = Object.keys(TYPES).filter((t) => counts[t]);
  return (
    <div className="flex h-full flex-col items-center justify-center px-6 text-center fade-up" data-testid="inbox-empty-state">
      {data?.items?.length ? (
        <>
          <div className="text-[12.5px] font-medium text-primary">{data.unread ? `${data.unread} uleste` : "Alt lest"}</div>
          <h1 className="mt-2 font-display text-[28px] font-bold tracking-[-0.03em] text-[hsl(var(--navy))]">Velg noe i innboksen</h1>
          <p className="mt-2 max-w-[420px] text-[14px] text-muted-foreground">Alt som trenger et menneske – forespørsler, kundesvar, godkjenninger og tilbud som åpnes – samlet ett sted.</p>
          <div className="mt-6 flex flex-wrap justify-center gap-2">
            {types.map((t) => { const Icon = ICON[t]; return <button key={t} type="button" onClick={() => onPick(t)} className="inline-flex h-10 items-center gap-2 rounded-full border border-black/[0.08] bg-white px-4 text-[13px] font-medium hover:border-black/[0.2]" data-testid={`inbox-empty-type-${t}`}><Icon className="h-4 w-4 text-primary" />{TYPES[t].label}<span className="font-mono text-[11px] tabular-nums text-muted-foreground">{counts[t]}</span></button>; })}
          </div>
          <div className="mt-10 flex flex-wrap justify-center gap-x-6 gap-y-2 text-[12px] text-muted-foreground">
            {[["J / K", "neste / forrige"], ["E", "ta inn i arbeid · arkiver"], ["U", "ulest"], ["↵", "åpne canvas"]].map(([k, d]) => <span key={k} className="inline-flex items-center gap-1.5"><span className="kbd">{k}</span>{d}</span>)}
          </div>
        </>
      ) : (
        <>
          <CheckCheck className="h-7 w-7 text-primary" />
          <h1 className="mt-4 font-display text-[26px] font-bold tracking-[-0.03em] text-[hsl(var(--navy))]">Alt er tatt hånd om</h1>
          <p className="mt-2 max-w-[380px] text-[14px] text-muted-foreground">Når et verft sender inn skjemaet, en kunde svarer eller et tilbud åpnes, dukker det opp her.</p>
        </>
      )}
    </div>
  );
}

/** Unified inbox. Desktop: list in the context panel, reader here. Mobile: list → reader. */
export default function InboxPage() {
  const { key } = useParams();
  const navigate = useNavigate();
  const isLg = useMediaQuery("(min-width: 1024px)");
  const { data } = useInbox();
  const f = useInboxFilter();
  const selected = key ? decodeURIComponent(key) : null;
  const items = useMemo(() => filterItems(data?.items, f.filter, f.q), [data, f.filter, f.q]);
  const idx = items.findIndex((x) => x.key === selected);
  const item = idx >= 0 ? items[idx] : (data?.items || []).find((x) => x.key === selected);
  const go = (i) => { const n = items[i]; if (n) navigate(keyPath(n.key), { replace: true }); else navigate("/innboks", { replace: true }); };
  const onPrev = idx > 0 ? () => go(idx - 1) : undefined;
  const onNext = idx >= 0 && idx < items.length - 1 ? () => go(idx + 1) : idx >= 0 ? () => go(idx) : undefined;

  useEffect(() => {
    if (selected && data && !item) navigate("/innboks", { replace: true });
  }, [selected, data, item, navigate]);

  useEffect(() => {
    const h = async (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      if (["input", "textarea", "select"].includes(tag) || e.target?.isContentEditable || e.metaKey || e.ctrlKey || e.altKey) return;
      if (document.querySelector("[role=dialog][data-state=open]")) return;
      const k = e.key.toLowerCase();
      if (k === "j" || k === "arrowdown") { e.preventDefault(); if (idx < 0) go(0); else if (onNext && idx < items.length - 1) onNext(); }
      else if (k === "k" || k === "arrowup") { e.preventDefault(); if (onPrev) onPrev(); }
      else if (k === "escape" && !isLg && selected) navigate("/innboks");
      else if (!item) return;
      else if (k === "u") markSeen([item.key], !item.seen);
      else if (k === "enter" || k === "o") navigate(`/p/${item.project.id}`);
      else if (k === "e") {
        if (item.type === "request") { try { const r = await api.post(`/inbox/${item.project.id}/intake`); toast.success("Assistenten leser forespørselen"); invalidate("/inbox"); navigate(`/ny/${r.data.session_id}`); } catch { toast.error("Kunne ikke ta inn forespørselen"); } }
        else { await archiveItems([item.key]); toast.success("Arkivert"); onNext?.(); }
      }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  });

  if (!isLg && !selected) {
    return (
      <div className="flex h-full min-h-0 flex-col" data-testid="inbox-page-mobile">
        <div className="flex items-baseline justify-between px-4 pb-2 pt-3">
          <h1 className="text-[17px] font-semibold tracking-[-0.012em]">Innboks</h1>
          <span className="text-[12px] text-muted-foreground">{data?.unread ? `${data.unread} uleste` : "Alt lest"}</span>
        </div>
        <InboxList {...f} onSelect={(it) => navigate(keyPath(it.key))} />
      </div>
    );
  }
  return (
    <div className="h-full min-h-0" data-testid="inbox-page">
      {item ? <InboxReader key={item.key} item={item} team={data?.team} onPrev={onPrev} onNext={onNext} index={idx < 0 ? 0 : idx} total={items.length} onBack={!isLg ? () => navigate("/innboks") : undefined} /> : <Empty data={data} onPick={(t) => { f.setFilter(t); const first = filterItems(data?.items, t, "")[0]; if (first) navigate(keyPath(first.key)); }} />}
    </div>
  );
}
